/**
 * useVfsFiles
 *
 * Subscribes to the unified `vfs.file.*` event stream and keeps a list
 * of files for one owner (`con-…` | `kb-…` | `skl-…`) in sync with the
 * backend. Cold-loads via `vfsApi.list(ownerId)` on mount + on WS
 * reconnect (to catch events missed during disconnect).
 *
 * Same merge pattern as AuiTaskDashboard's delegated-task discovery:
 * keep a Map keyed by fileId, reconcile API results with WS-discovered
 * entries (whichever arrives first wins, later updates overwrite).
 */

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import { vfsApi } from '../services/api';
import type { PlatformEvent, VfsFileEventData, VfsFileMovedEventData } from '../types/events';
import type { VfsEntry } from '../types/vfs';

export interface UseVfsFilesResult {
  entries: VfsEntry[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useVfsFiles(ownerId: string | undefined): UseVfsFilesResult {
  const { subscribe, isConnected } = usePlatformEvents();
  const [files, setFiles] = useState<Map<string, VfsEntry>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!ownerId) return;
    try {
      const { items } = await vfsApi.list(ownerId);
      setFiles((prev) => {
        const next = new Map<string, VfsEntry>();
        for (const e of items) next.set(e.fileId, e);
        // Keep WS-discovered entries that haven't appeared in the list yet
        // (e.g. created in the gap between subscription and list response).
        for (const [id, e] of prev) {
          if (!next.has(id)) next.set(id, e);
        }
        return next;
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    }
  }, [ownerId]);

  // Cold-load on mount / when ownerId changes.
  useEffect(() => {
    if (!ownerId) {
      setFiles(new Map());
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    refetch().finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [ownerId, refetch]);

  // Re-fetch on reconnection to catch events missed during disconnect.
  const wasConnectedRef = useRef(isConnected);
  useEffect(() => {
    const wasConnected = wasConnectedRef.current;
    wasConnectedRef.current = isConnected;
    if (!wasConnected && isConnected && ownerId) {
      refetch();
    }
  }, [isConnected, ownerId, refetch]);

  // Live updates via WS.
  useEffect(() => {
    if (!ownerId) return;
    // True when an event belongs to this owner. Bare `vfs.file.*` events are
    // already `subjectId`-filtered to `ownerId`, but cross-store copies (a KB
    // file dragged into this conversation) arrive in the `kb.vfs.*` namespace
    // carrying the owner in `subjectId`/`data.ownerId` rather than via a
    // subjectId filter — so match on either. Mirrors the KB reconciler's
    // `ownerMatch` (files-backend/kb.ts).
    const ownerMatches = (dataOwnerId: string | undefined, subjectId: string | undefined) =>
      dataOwnerId === ownerId || subjectId === ownerId;
    const unsubscribe = subscribe(
      [
        { eventType: 'vfs.file.created', subjectId: ownerId },
        { eventType: 'vfs.file.updated', subjectId: ownerId },
        { eventType: 'vfs.file.deleted', subjectId: ownerId },
        { eventType: 'vfs.file.moved', subjectId: ownerId },
        // Cross-store copies into this owner emit `kb.vfs.*` file events.
        // Subscribed broadly (no subjectId) and scoped by `ownerMatches`.
        { eventType: 'kb.vfs.file.created' },
        { eventType: 'kb.vfs.file.updated' },
        { eventType: 'kb.vfs.file.deleted' },
        { eventType: 'kb.vfs.file.moved' },
      ],
      (event: PlatformEvent) => {
        const t = event.eventType;
        // *.file.moved carries a different payload shape (fromPath /
        // toPath); handle it before the generic VfsFileEventData branch.
        if (t === 'vfs.file.moved' || t === 'kb.vfs.file.moved') {
          const m = event.data as unknown as VfsFileMovedEventData;
          if (!m?.fileId) return;
          if (!ownerMatches(m.ownerId, event.subjectId)) return;
          setFiles((prev) => {
            const existing = prev.get(m.fileId);
            if (!existing) return prev;
            const next = new Map(prev);
            next.set(m.fileId, {
              ...existing,
              path: m.toPath,
              name: m.name,
              size: m.size,
              contentType: m.contentType,
              updatedAt: m.updatedAt,
              etag: m.etag,
            });
            return next;
          });
          return;
        }

        const data = event.data as unknown as VfsFileEventData;
        if (!data?.fileId) return;
        // Scope to this owner — the `kb.vfs.*` filters above aren't
        // subjectId-filtered, so other owners' events reach here too.
        if (!ownerMatches(data.ownerId, event.subjectId)) return;

        if (t === 'vfs.file.deleted' || t === 'kb.vfs.file.deleted') {
          setFiles((prev) => {
            if (!prev.has(data.fileId)) return prev;
            const next = new Map(prev);
            next.delete(data.fileId);
            return next;
          });
          return;
        }

        // created / updated → upsert
        setFiles((prev) => {
          const next = new Map(prev);
          next.set(data.fileId, {
            fileId: data.fileId,
            ownerId: data.ownerId ?? ownerId,
            path: data.path,
            name: data.name,
            size: data.size,
            contentType: data.contentType,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            createdBy: data.createdBy,
            etag: data.etag,
          });
          return next;
        });
      },
    );
    return unsubscribe;
  }, [ownerId, subscribe]);

  // Memoize so consumers can use `entries` as a useMemo/useEffect
  // dependency without thrashing on every parent re-render. The Map
  // identity already gates this — it only changes when an event mutates
  // an entry, so `entries` is stable across unrelated re-renders.
  const entries = useMemo(() => Array.from(files.values()), [files]);

  return {
    entries,
    isLoading,
    error,
    refetch,
  };
}
